import { getIsWidget } from 'js/helpers/page-data';
import {
  MPInitOptions,
  MPUserIdentities,
  mParticleInit,
  mParticleLogEvent,
  mParticleEventType,
} from '@treatwell/ui';
import {
  getDeviceId,
  growthbookInitialiseClient,
} from '@treatwell/growthbook-js/client';
import { twEnvTypeValue, isTest } from './helpers/environment';
import { getClientGrowthbookConfig } from '../js/growthbook/growthbook-config';
import { configureSentry } from './sentry-config';

let growthbookResolve: (value: any) => void;
const growthbookPromise = new Promise((resolve) => {
  growthbookResolve = resolve;
});

export function getGrowthbook() {
  return growthbookPromise;
}

let brazeResolve: (value: boolean) => void;
const brazePromise = new Promise<boolean>((resolve) => {
  brazeResolve = resolve;
});

export function getBraze() {
  return brazePromise;
}

export async function initialiseCoreLibraries() {
  const channel = window.__state__.channel;
  const accountDetails = window.__state__.accountDetails;
  const isAuthenticated =
    accountDetails.loginStatus === 'account_authenticated';

  const growthbookConfig = getClientGrowthbookConfig(twEnvTypeValue);
  const deviceId = await getDeviceId();

  initMParticle(deviceId);

  configureSentry();

  const growthbook = await growthbookInitialiseClient(
    growthbookConfig,
    deviceId,
    {
      growthbookAttributes: {
        channel: {
          country: channel.country.countryCode,
        },
        user: {
          account_id: String(accountDetails.accountId),
          is_authenticated: isAuthenticated,
        },
      },
      deferredTrackingCalls: window.deferredTrackingCalls,
      activationEvent: {
        type: 'mparticle',
        send(defaultAttributes) {
          mParticleLogEvent(
            '$experiment_started',
            mParticleEventType.Other,
            defaultAttributes
          );
        },
      },
    }
  );

  growthbookResolve(growthbook);
}

async function initMParticle(deviceId: string) {
  const state = window.__state__;
  const isWidget = getIsWidget(window.__state__);
  const {
    channel: { country, languageCode },
    accountDetails: { accountId, email },
  } = state;

  if (isWidget || isTest()) {
    return;
  }

  try {
    // Download the mParticle SDK before the kits, since the kits depend on it
    const mParticle = await import(
      /* webpackChunkName: "mparticle_web-sdk" */ '@mparticle/web-sdk'
    );
    const [onetrustKit, brazeKit] = await Promise.all([
      import(
        /* webpackChunkName: "mparticle_web-onetrust-kit" */ '@mparticle/web-onetrust-kit'
      ),
      import(
        /* webpackChunkName: "mparticle_web-braze-kit" */ '@mparticle/web-braze-kit'
      ),
    ]);

    mParticle.default.ready(() => {
      const user = window.braze?.getUser();

      if (user) {
        user.setLanguage(languageCode);

        brazeResolve(true);
      } else {
        brazeResolve(false);
      }
    });

    const options: MPInitOptions = {
      isDevelopmentMode: twEnvTypeValue !== 'prod',
      deviceId,
      identityCallback: (result) => {
        result.getUser().setUserAttribute('$Country', country.countryCode);
        result.getUser().setUserAttribute('language', languageCode);
      },
    };
    const userIdentities: MPUserIdentities = {};

    if (accountId) {
      userIdentities.customerid = `${accountId}`;

      if (email) {
        userIdentities.email = email;
      }
    }

    // Register kits before initializing mParticle,
    // so that when mParticle starts, it initialises the kits automatically
    onetrustKit.register(options);
    brazeKit.default.register(options);

    mParticleInit(mParticle.default, options, userIdentities).catch(() => {
      brazeResolve(false);
    });
  } catch (error) {
    brazeResolve(false);

    console.error('Error initializing mParticle:', error);
  }
}
